// utils/analytics.js
// Google Analytics utility functions for conversion tracking

/**
 * Track a signup conversion event
 * @param {Object} params - Conversion parameters
 * @param {string} params.method - Signup method ('email' or 'google')
 * @param {string} params.plan - Subscription plan (e.g., 'FREE', 'STARTER')
 * @param {string} params.userId - User ID (optional)
 * @param {boolean} params.isNewUser - Whether this is a new user (default: true)
 */
export const trackSignupConversion = (params = {}) => {
  const {
    method = 'email',
    plan = 'FREE',
    userId = null,
    isNewUser = true
  } = params;

  // Only track conversions for new users
  if (!isNewUser) {
    console.log('Analytics: Skipping conversion - existing user login');
    return;
  }

  // Check if user already converted (prevent duplicate conversions)
  const hasConverted = localStorage.getItem('ga_conversion_tracked');
  if (hasConverted) {
    console.log('Analytics: Skipping conversion - already tracked');
    return;
  }

  // Track GA4 sign_up event (recommended event)
  if (window.gtag) {
    // Standard GA4 sign_up event
    window.gtag('event', 'sign_up', {
      method: method,
      plan: plan,
      user_id: userId,
      event_category: 'engagement',
      event_label: `${method}_signup`
    });

    // Google Ads conversion event (if you have conversion ID/label)
    // Replace 'AW-CONVERSION_ID' with your actual conversion ID
    // Replace 'CONVERSION_LABEL' with your actual conversion label
    window.gtag('event', 'conversion', {
      'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL',
      'value': 0.0,
      'currency': 'USD',
      'transaction_id': userId || Date.now().toString()
    });

    console.log('✅ Analytics: Signup conversion tracked', {
      method,
      plan,
      userId
    });

    // Mark conversion as tracked
    localStorage.setItem('ga_conversion_tracked', 'true');
  } else {
    console.warn('⚠️ Analytics: gtag not available - conversion not tracked');
  }
};

/**
 * Track a subscription upgrade conversion event (FREE to paid plan)
 * @param {Object} params - Upgrade parameters
 * @param {string} params.plan - New subscription plan (e.g., 'STARTER', 'GROWTH', 'SCALE', 'ENTERPRISE')
 * @param {string} params.previousPlan - Previous plan (e.g., 'FREE')
 * @param {number} params.value - Transaction value (optional)
 * @param {string} params.userId - User ID (optional)
 */
export const trackUpgradeConversion = (params = {}) => {
  const {
    plan = 'STARTER',
    previousPlan = 'FREE',
    value = 0,
    userId = null
  } = params;

  // Only track if upgrading from FREE to a paid plan
  if (previousPlan !== 'FREE' || plan === 'FREE') {
    console.log('Analytics: Skipping upgrade tracking - not a FREE to paid upgrade', {
      previousPlan,
      plan
    });
    return;
  }

  // Check if upgrade already tracked (prevent duplicates)
  const hasUpgraded = localStorage.getItem('ga_upgrade_tracked');
  if (hasUpgraded) {
    console.log('Analytics: Skipping upgrade - already tracked');
    return;
  }

  // Track GA4 purchase event (recommended for subscriptions)
  if (window.gtag) {
    // Standard GA4 purchase event
    window.gtag('event', 'purchase', {
      transaction_id: `upgrade_${userId || Date.now()}`,
      value: value,
      currency: 'USD',
      items: [{
        item_id: plan,
        item_name: `${plan} Plan`,
        item_category: 'subscription',
        price: value,
        quantity: 1
      }]
    });

    // Custom upgrade event
    window.gtag('event', 'subscription_upgrade', {
      plan: plan,
      previous_plan: previousPlan,
      value: value,
      user_id: userId,
      event_category: 'conversion',
      event_label: `${previousPlan}_to_${plan}`
    });

    // Google Ads conversion event for upgrades
    // Replace 'AW-UPGRADE_CONVERSION_ID' with your actual upgrade conversion ID
    // Replace 'UPGRADE_CONVERSION_LABEL' with your actual upgrade conversion label
    window.gtag('event', 'conversion', {
      'send_to': 'AW-UPGRADE_CONVERSION_ID/UPGRADE_CONVERSION_LABEL',
      'value': value,
      'currency': 'USD',
      'transaction_id': `upgrade_${userId || Date.now()}`
    });

    console.log('✅ Analytics: Upgrade conversion tracked', {
      plan,
      previousPlan,
      value,
      userId
    });

    // Mark upgrade as tracked
    localStorage.setItem('ga_upgrade_tracked', 'true');
  } else {
    console.warn('⚠️ Analytics: gtag not available - upgrade not tracked');
  }
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {Object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
    console.log(`Analytics: Event tracked - ${eventName}`, eventParams);
  } else {
    console.warn('⚠️ Analytics: gtag not available');
  }
};

/**
 * Track a page view
 * @param {string} pageTitle - Page title
 * @param {string} pagePath - Page path
 */
export const trackPageView = (pageTitle, pagePath) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: pageTitle,
      page_path: pagePath
    });
  }
};

/**
 * Clear conversion tracking flags (useful for testing)
 */
export const clearConversionTracking = () => {
  localStorage.removeItem('ga_conversion_tracked');
  localStorage.removeItem('ga_upgrade_tracked');
  console.log('Analytics: Conversion tracking flags cleared');
};
